import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Award,
  BarChart3,
  Calendar,
  Check,
  Crown,
  HelpCircle,
  Lock,
  Share2,
  Star,
  Trophy,
  User,
  X
} from 'lucide-react';
import { FaToilet } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';
import firebaseService from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';
import analytics from '../utils/analytics';
import { getRelativeTime } from '../utils/timeUtils';
import {
  getBadgeImagePath,
  getBadgeLockImagePath,
} from '../utils/badgeAssets';
import BadgeHelpContent, { TIER_COLOR } from './BadgeHelpContent';

// Ordered list of badge categories, matching the badge asset naming scheme:
// [restroomcategory]_[BadgeName]_[BadgeColor]_100.png
const BADGE_CATEGORIES = [
  {
    id: 'bars_nightclubs',
    label: 'Bars / Nightclubs',
    tiers: { bronze: 'happy_hour', silver: 'buzzed_bathroomer', gold: 'taproom_titan' }
  },
  {
    id: 'transit',
    label: 'Transit',
    tiers: { bronze: 'transit_tummy', silver: 'terminal_titan', gold: 'world_wiper' }
  },
  {
    id: 'restaurant',
    label: 'Restaurants',
    tiers: { bronze: 'food_flusher', silver: 'fork_and_flush', gold: 'michelin_throne' }
  },
  {
    id: 'stadium',
    label: 'Stadiums',
    tiers: { bronze: 'game_time', silver: 'pro_goer', gold: 'most_valuable_porcelain' }
  },
  {
    id: 'office',
    label: 'Office',
    tiers: { bronze: 'working_overtime', silver: 'office_insider', gold: 'bathroom_boss' }
  },
  {
    id: 'coffee_shop',
    label: 'Coffee Shops',
    tiers: { bronze: 'bean_boss', silver: 'coffee_connoisseur', gold: 'latte_legend' }
  },
  {
    id: 'park',
    label: 'Parks',
    tiers: { bronze: 'forest_flush', silver: 'porcelain_ranger', gold: 'natures_throne' }
  },
  {
    id: 'retail',
    label: 'Retail',
    tiers: { bronze: 'retail_relief', silver: 'mall_marker', gold: 'shopping_savior' }
  },
  {
    id: 'gas_station',
    label: 'Gas Stations',
    tiers: { bronze: 'dump_and_run', silver: 'highway_hero', gold: 'fueling_the_flush' }
  },
  {
    id: 'truck_stop',
    label: 'Truck Stops',
    tiers: { bronze: 'road_relief', silver: 'diesel_defender', gold: 'truck_stop_king' }
  },
  {
    id: 'fast_food',
    label: 'Fast Food',
    assetId: 'fastfood', // asset files use `fastfood_` prefix (no underscore)
    tiers: { bronze: 'quick_relief', silver: 'meal_feeder', gold: 'fast_food_king' }
  },
  {
    id: 'grocery_store',
    label: 'Grocery Store',
    tiers: { bronze: 'aisle_relief', silver: 'pantry_patron', gold: 'grocery_goat' }
  },
  {
    id: 'public_facility',
    label: 'Public Facility',
    tiers: { bronze: 'public_provider', silver: 'community_champ', gold: 'public_presenter' }
  },
  {
    id: 'hotel_motel',
    label: 'Hotel/Motel',
    tiers: { bronze: 'roomie', silver: 'hotel_savior', gold: 'hospitality_hero' }
  },
  {
    id: 'other',
    label: 'Other',
    tiers: { bronze: 'mystery_relief', silver: 'unsung', gold: 'beyond_and_beyond' }
  }
];

const TIER_THRESHOLDS = [
  { tier: 'gold', threshold: 25 },
  { tier: 'silver', threshold: 10 },
  { tier: 'bronze', threshold: 1 }
];

// Badge help content (progression example, explainer, legend) lives in
// BadgeHelpContent.js, shared with the account-creation tutorial.

// Badge artwork now resolves through the shared utils (generic per-tier
// SVG files) — see src/utils/badgeAssets.js.

const TIER_ORDER = ['bronze', 'silver', 'gold'];
const TIER_REQUIREMENT = { bronze: 1, silver: 10, gold: 25 };

// Determine the highest tier a user has unlocked for a category, based on the
// badge id's stored on the user (e.g. `bars_nightclubs_10`).
function getHighestTierForCategory(categoryId, unlockedThresholds) {
  const thresholds = unlockedThresholds[categoryId] || new Set();
  for (const { tier, threshold } of TIER_THRESHOLDS) {
    if (thresholds.has(threshold)) return tier;
  }
  return null;
}

function getUnlockedThresholdsByCategory(badges) {
  const map = {};
  badges.forEach((badge) => {
    const badgeId = typeof badge === 'string' ? badge : badge?.id;
    if (!badgeId || typeof badgeId !== 'string') return;

    const match = badgeId.match(/^(.*)_(1|10|25)$/);
    if (!match) return;

    const [, categoryId, thresholdStr] = match;
    if (!map[categoryId]) map[categoryId] = new Set();
    map[categoryId].add(Number(thresholdStr));
  });
  return map;
}

// Mirrors the backend normalizer (shared/gamificationProcessor.js) so that a
// restroom's kebab-case category value maps to the same underscore badge id
// that the badge counters use (e.g. 'gas-station' -> 'gas_station',
// 'bar' -> 'bars_nightclubs').
const CATEGORY_ALIASES = {
  coffee_shops: 'coffee_shop',
  coffeeshop: 'coffee_shop',
  gasstation: 'gas_station',
  gas_stations: 'gas_station',
  convenience_store: 'gas_station',
  bars_nightclubs: 'bars_nightclubs',
  barsnightclubs: 'bars_nightclubs',
  bar_nightclub: 'bars_nightclubs',
  bar_nightclubs: 'bars_nightclubs',
  bar: 'bars_nightclubs',
  parks: 'park',
  restaurants: 'restaurant',
  hotels: 'hotel_motel',
  hotel: 'hotel_motel',
  groceries: 'grocery_store',
  grocery: 'grocery_store',
  arena_stadium: 'stadium',
  arena: 'stadium'
};

function normalizeCategoryKey(rawKey) {
  if (!rawKey || typeof rawKey !== 'string') return null;
  const normalized = rawKey.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return CATEGORY_ALIASES[normalized] || normalized;
}

function formatJoinedDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// Milliseconds since epoch for sorting, handling Firestore Timestamps, ISO
// strings, {seconds} objects, and Dates. Missing/invalid dates sort to the end.
function getSortTimestamp(value) {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).getTime();
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

// Fallback TP progression ladder, mirrors the titles configured in Firestore
// (system_config/gamification_rules.titles). Used only if the rules can't be fetched.
const DEFAULT_TITLE_LADDER = [
  { minTP: 0, name: 'Newcomer' },
  { minTP: 100, name: 'Flush Scout' },
  { minTP: 300, name: 'Restroom Ranger' },
  { minTP: 700, name: 'Porcelain Pioneer' },
  { minTP: 1250, name: 'Flush Master' },
  { minTP: 2000, name: 'Bathroom Baron' },
  { minTP: 3500, name: 'Throne Guardian' },
  { minTP: 5000, name: 'Royal Flush' }
];

const Container = styled.div`
  min-height: 100vh;
  background: #ffffff;
  color: #231F20;
  padding: 20px;

  // Clear the fixed bottom ad banner (body class set by AppDownloadBanner).
  // Desktop-only: the banner is display:none on mobile.
  @media (min-width: 769px) {
    body.has-bottom-banner & {
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    }
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 50;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  color: #231F20;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  /* Mobile: clear the fixed 50px top app banner when it's showing
     (body class toggled by MobileAppDownloadModal). */
  @media (max-width: 768px) {
    body.has-top-banner & {
      top: calc(50px + 12px + env(safe-area-inset-top, 0px));
    }
  }
`;

const Card = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
`;

const ProfileTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  flex-wrap: wrap;
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const Avatar = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: #faaf40;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border: 3px solid #ffe0a3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const IdentityInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Username = styled.h1`
  margin: 0 0 8px;
  font-size: 2rem;
  color: #ffffff;
`;

const TitleBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #faaf40;
  color: #4a3200;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 8px;
  align-self: flex-start;
`;

const JoinedDate = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.65);
`;

const RightActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 600px) {
    align-items: flex-start;
  }
`;

const ShareButton = styled.button`
  background: #5752e8;
  border: 1px solid #5752e8;
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: background 0.2s;

  &:hover {
    background: #5752e8;
    filter: brightness(0.88);
  }
`;

const ProfileBranding = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  font-weight: 600;
`;

const BrandingLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  border-radius: 4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 22px;

  /* Mobile: single compact row of 4 so the stats stop eating half the
     viewport (2x2 large cards -> 1x4 small cards). */
  @media (max-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const Stat = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  ${(props) =>
    props.$clickable &&
    `
    &:hover {
      background: #f0f0f0;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}

  @media (max-width: 600px) {
    padding: 10px 6px;
    border-radius: 10px;
  }
`;

const RestroomsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
`;

const RestroomCard = styled.div`
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1e1e1e;
    border-color: #2d6657;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

const RestroomCardTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 1.05rem;
  color: #ffffff;
`;

const RestroomCardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const RestroomRating = styled.div`
  font-size: 0.95rem;
`;

const ReviewsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
`;

const ReviewCard = styled.div`
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1e1e1e;
    border-color: #faaf40;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

const ReviewCardTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 1.05rem;
  color: #ffffff;
`;

const ReviewCardRating = styled.div`
  font-size: 1rem;
  margin-bottom: 6px;
`;

const ReviewCardComment = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const ReviewCardDate = styled.small`
  display: block;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
`;

const Label = styled.div`
  font-size: 0.82rem;
  color: rgba(35, 31, 32, 0.6);
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  @media (max-width: 600px) {
    font-size: 0.62rem;
    gap: 4px;
    line-height: 1.25;
  }
`;

const StatIconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$color || '#614f48'};
  color: #ffffff;

  @media (max-width: 600px) {
    width: 26px;
    height: 26px;
  }
`;

const Value = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #231F20;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const BadgesAndProgressionRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const BadgesColumn = styled.div`
  flex: 1 1 60%;
  min-width: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px 14px;

  /* Mobile: badges render above the TP progression ladder. */
  @media (max-width: 720px) {
    order: -1;
    width: 100%;
  }
`;

const ProgressionColumn = styled.div`
  flex: 1 1 40%;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px 14px;

  @media (max-width: 720px) {
    width: 100%;
    min-width: 0;
  }
`;

const LadderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LadderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${(props) => (props.$active ? '#faaf40' : '#f5f5f5')};
  border: 1px solid ${(props) => (props.$active ? '#faaf40' : '#e0e0e0')};
`;

const LadderName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${(props) => (props.$active ? 700 : 500)};
  color: ${(props) => (props.$active ? '#4a3200' : props.$reached ? '#231F20' : 'rgba(35, 31, 32, 0.45)')};
  font-size: 0.9rem;
`;

const LadderTP = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#4a3200' : props.$reached ? 'rgba(35, 31, 32, 0.75)' : 'rgba(35, 31, 32, 0.4)')};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 1rem;
  color: #231F20;
`;

const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
`;

const BadgeItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const BadgeIconWrap = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${(props) => (props.$locked ? '#e9ecef' : 'transparent')};
  border: ${(props) => (props.$locked ? '2px dashed rgba(35, 31, 32, 0.35)' : 'none')};

  img {
    // Locked art is full-bleed: inset it so the dashed circle neither
    // overlaps nor clips the artwork.
    width: ${(props) => (props.$locked ? '72%' : '100%')};
    height: ${(props) => (props.$locked ? '72%' : '100%')};
    object-fit: contain;
  }
`;

const BadgeCategoryLabel = styled.div`
  font-size: 0.78rem;
  color: ${(props) => (props.$locked ? 'rgba(35, 31, 32, 0.5)' : '#231F20')};
`;

const CategoryRestroomsSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const CategoryRestroomsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #231F20;
  margin-bottom: 10px;
`;

const CategoryRestroomsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const CategoryRestroomItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  span {
    font-size: 0.9rem;
    font-weight: 600;
    color: #231F20;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    font-size: 0.75rem;
    color: rgba(35, 31, 32, 0.6);
    flex-shrink: 0;
  }
`;

const CategoryRestroomsEmpty = styled.p`
  font-size: 0.85rem;
  color: rgba(35, 31, 32, 0.6);
  margin: 0;
`;

const Message = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background: #141414;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  color: #ffffff;
`;

const HelpLink = styled.button`
  background: none;
  border: none;
  color: #faaf40;
  font-size: 0.82rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  /* Gold-on-white is low contrast: darken for legibility (no new token). */
  filter: brightness(0.82);

  &:hover {
    color: #faaf40;
  }
`;

const ShareToast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a1a;
  color: #ffffff;
  padding: 10px 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #0a0a0a;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  max-width: 780px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 24px;
  position: relative;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #2a2a2a;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;

  &:hover {
    background: #3a3a3a;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 1.4rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BadgeButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  width: 100%;
`;

const DetailBadgeWrap = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0 auto 16px;
  background: ${(props) => (props.$locked ? '#2a2a2a' : 'transparent')};
  border: ${(props) => (props.$locked ? '2px dashed rgba(255, 255, 255, 0.35)' : 'none')};

  img {
    // Same inset as the grid: full-bleed lock art must clear the circle.
    width: ${(props) => (props.$locked ? '72%' : '100%')};
    height: ${(props) => (props.$locked ? '72%' : '100%')};
    object-fit: contain;
  }
`;

const DetailTierBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${(props) => props.$color || '#f4f4f4'};
  color: #ffffff;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 6px;
`;

const DetailTierName = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 12px;
`;

// Green bubble fill matching the gamification popup progress bar.
const ProgressBarTrack = styled.div`
  width: 100%;
  height: 14px;
  border-radius: 999px;
  background: #e0e0e0;
  overflow: hidden;
  margin: 10px 0 8px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #2d6657;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
`;

const NextTierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const NextTierImg = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
  opacity: 0.6;
`;

const NextTierText = styled.div`
  font-size: 0.85rem;

  strong {
    display: block;
    color: #ffffff;
    text-transform: capitalize;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const MaxedText = styled.div`
  margin-top: 18px;
  text-align: center;
  font-size: 0.9rem;
  color: #ffffff;
  font-weight: 600;
`;

function UserProfileView() {
  const navigate = useNavigate();
  const { identifier } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [rankDisplay, setRankDisplay] = useState('updating');
  const [titleLadder, setTitleLadder] = useState(DEFAULT_TITLE_LADDER);
  const [badgeTierNames, setBadgeTierNames] = useState({});
  const [shareMessage, setShareMessage] = useState('');
  const [showBadgesHelp, setShowBadgesHelp] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showRestroomsModal, setShowRestroomsModal] = useState(false);
  const [userRestrooms, setUserRestrooms] = useState(null);
  const [loadingRestrooms, setLoadingRestrooms] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [userReviews, setUserReviews] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);

  const decodedIdentifier = useMemo(() => decodeURIComponent(identifier || ''), [identifier]);
  // Dedupe the analytics event: React StrictMode double-invokes effects in
  // development, and re-fetches for the same identifier should not re-fire.
  const visitTrackedFor = useRef(null);
  // Read the viewer via a ref so the profile-load effect doesn't depend on
  // currentUser identity (which would refetch the profile on every refresh).
  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      setLoading(true);
      setError('');
      setUserRestrooms(null);
      setUserReviews(null);

      try {
        let user = await firebaseService.getUserByDocumentId(decodedIdentifier);
        if (!user) {
          user = await firebaseService.getUserByUsername(decodedIdentifier);
        }

        if (!user) {
          if (isMounted) setError('Profile not found.');
          return;
        }

        if (!isMounted) return;
        setProfile(user);

        // Analytics: profile view, flagged as the viewer's own or someone
        // else's, keyed by the profile owner's user id.
        if (visitTrackedFor.current !== user.id) {
          visitTrackedFor.current = user.id;
          analytics.track('my_profile_visited', {
            profile_user_id: user.id,
            is_own_profile: !!currentUserRef.current && currentUserRef.current.id === user.id,
          });
        }

        try {
          const functions = getFunctions();
          const getGamificationStats = httpsCallable(functions, 'GetUserGamificationStats');
          const statsResult = await getGamificationStats({
            userId: user.id,
            database_id_hash: localStorage.getItem('database_id_hash') || ''
          });

          if (isMounted && statsResult.data?.success) {
            setGamification(statsResult.data);
          }
        } catch (statsError) {
          console.warn('Failed to fetch gamification stats:', statsError);
        }

        try {
          const functions = getFunctions();
          const getRules = httpsCallable(functions, 'GetGamificationRules');
          const rulesResult = await getRules({
            database_id_hash: localStorage.getItem('database_id_hash') || ''
          });

          if (isMounted && rulesResult.data?.success) {
            const rules = rulesResult.data.rules || {};
            if (Array.isArray(rules.titles)) {
              const sortedTitles = [...rules.titles].sort((a, b) => a.minTP - b.minTP);
              setTitleLadder(sortedTitles);
            }
            if (rules.badges?.categoryTiers && typeof rules.badges.categoryTiers === 'object') {
              setBadgeTierNames(rules.badges.categoryTiers);
            }
          }
        } catch (rulesError) {
          console.warn('Failed to fetch gamification rules, using default title ladder:', rulesError);
        }

        try {
          const functions = getFunctions();
          const getRankEstimate = httpsCallable(functions, 'GetTPRankEstimate');
          const rankCall = getRankEstimate({
            userId: user.id,
            totalTP: Number(user.totalTP || 0),
            database_id_hash: localStorage.getItem('database_id_hash') || ''
          });
          // Swallow late rejections: the timeout below may settle first.
          rankCall.catch(() => {});
          // U3 — never hang on "updating": 12s timeout → em-dash fallback.
          let rankTimer;
          const timeoutPromise = new Promise((_, reject) => {
            rankTimer = setTimeout(() => reject(new Error('rank-timeout')), 12000);
          });
          try {
            const result = await Promise.race([rankCall, timeoutPromise]);

            if (isMounted && result.data?.success) {
              setRankDisplay(result.data.rankDisplay || result.data.percentile || '—');
            } else if (isMounted) {
              setRankDisplay('—');
            }
          } finally {
            clearTimeout(rankTimer);
          }
        } catch (rankError) {
          console.warn('Failed to fetch TP rank estimate:', rankError);
          if (isMounted) setRankDisplay('—');
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || 'Failed to load profile.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [decodedIdentifier]);

  const totalTP = gamification?.totalTP ?? Number(profile?.totalTP || 0);
  const title = gamification?.title || profile?.title || 'Newcomer';
  const unlockedThresholdsByCategory = useMemo(() => {
    const badges = Array.isArray(gamification?.badges)
      ? gamification.badges
      : (Array.isArray(profile?.badges) ? profile.badges : []);
    return getUnlockedThresholdsByCategory(badges);
  }, [gamification?.badges, profile?.badges]);
  const categoryCounts = gamification?.categoryCounts && typeof gamification.categoryCounts === 'object'
    ? gamification.categoryCounts
    : {};

  const joinedDate = formatJoinedDate(profile?.createdAt || profile?.joinedDate);

  const selectedCategory = selectedCategoryId
    ? BADGE_CATEGORIES.find((category) => category.id === selectedCategoryId)
    : null;

  const categoryRestrooms = useMemo(() => {
    if (!selectedCategory || !Array.isArray(userRestrooms)) return [];
    return userRestrooms
      .filter((restroom) => normalizeCategoryKey(restroom.category) === selectedCategory.id)
      .sort((a, b) => (new Date(b.createdAt).getTime() || 0) - (new Date(a.createdAt).getTime() || 0));
  }, [selectedCategory, userRestrooms]);

  const ensureUserRestroomsLoaded = async () => {
    const userId = profile?.id || profile?.uid || profile?.firebaseUid;
    if (!userId || userRestrooms !== null) return;
    setLoadingRestrooms(true);
    try {
      const restrooms = await firebaseService.getUserRestrooms(userId);
      setUserRestrooms(restrooms);
    } catch (err) {
      console.error('Error loading user restrooms:', err);
      setUserRestrooms([]);
    } finally {
      setLoadingRestrooms(false);
    }
  };

  const handleOpenRestroomsModal = async () => {
    setShowRestroomsModal(true);
    await ensureUserRestroomsLoaded();
  };

  const handleRestroomClick = (restroom) => {
    setShowRestroomsModal(false);
    setSelectedCategoryId(null);
    navigate('/app', { state: { selectedRestroom: restroom, selectedRestroomId: restroom.id, zoom: 17 } });
  };

  const handleOpenReviewsModal = async () => {
    setShowReviewsModal(true);
    const userId = profile?.id || profile?.uid || profile?.firebaseUid;
    if (userId && userReviews === null) {
      setLoadingReviews(true);
      try {
        const reviews = await firebaseService.getUserReviews(userId);
        setUserReviews(reviews);
      } catch (err) {
        console.error('Error loading user reviews:', err);
        setUserReviews([]);
      } finally {
        setLoadingReviews(false);
      }
    }
  };

  const handleReviewClick = (review) => {
    setShowReviewsModal(false);
    navigate('/app', { state: { selectedRestroomId: review.restroomId, zoom: 17 } });
  };

  const restroomsAddedCount = Number(
    userRestrooms ? userRestrooms.length : (profile?.restroomsAdded || profile?.restroomsCreated || 0)
  );
  const restroomsReviewedCount = Number(
    userReviews ? userReviews.length : (profile?.restroomsReviewed || profile?.reviewsGiven || 0)
  );

  // Newest first for both profile lists (dates can be Timestamps, ISO strings,
  // or {seconds} objects). Sorted copies — never mutates the fetched state.
  const sortedUserRestrooms = useMemo(() => {
    if (!Array.isArray(userRestrooms)) return [];
    return [...userRestrooms].sort((a, b) => getSortTimestamp(b.createdAt) - getSortTimestamp(a.createdAt));
  }, [userRestrooms]);

  const sortedUserReviews = useMemo(() => {
    if (!Array.isArray(userReviews)) return [];
    return [...userReviews].sort((a, b) => getSortTimestamp(b.createdAt) - getSortTimestamp(a.createdAt));
  }, [userReviews]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${profile?.username || 'Profile'} on CrapperMapper`, url: shareUrl });
        return;
      } catch (shareError) {
        // fall through to clipboard copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Profile link copied to clipboard!');
    } catch (copyError) {
      console.warn('Failed to copy profile link:', copyError);
      setShareMessage('Could not copy link. Please copy it manually.');
    }

    setTimeout(() => setShareMessage(''), 3000);
  };

  return (
    <Container>
      <CloseButton onClick={() => navigate(-1)} aria-label="Close profile">
        <X size={20} />
      </CloseButton>

      {loading ? (
        <Message>Loading profile...</Message>
      ) : error ? (
        <Message>{error}</Message>
      ) : !profile ? (
        <Message>Profile unavailable.</Message>
      ) : (
        <Card>
          <ProfileTop>
            <IdentityRow>
              <Avatar>
                {profile.photoURL ? (
                  <img src={profile.photoURL} alt={profile.username || 'Profile avatar'} />
                ) : (
                  <User size={38} color="#ffffff" />
                )}
              </Avatar>
              <IdentityInfo>
                <Username>{profile.username || 'anonymous'}</Username>
                <TitleBanner>
                  <Crown size={14} />
                  {title.toUpperCase()}
                </TitleBanner>
                {joinedDate && (
                  <JoinedDate>
                    <Calendar size={14} />
                    Joined {joinedDate}
                  </JoinedDate>
                )}
              </IdentityInfo>
            </IdentityRow>

            <RightActionContainer>
              <ProfileBranding>
                <BrandingLogo src="/app-icon.png" alt="CrapperMapper logo" />
                <span>CrapperMapper</span>
              </ProfileBranding>
              <ShareButton onClick={handleShare}>
                <Share2 size={16} />
                Share Profile
              </ShareButton>
            </RightActionContainer>
          </ProfileTop>

          <Grid>
            <Stat>
              <Label>
                <StatIconWrap $color="#faaf40"><Trophy size={16} /></StatIconWrap>
                Total TP
              </Label>
              <Value>{Number(totalTP || 0).toLocaleString()}</Value>
            </Stat>
            <Stat>
              <Label>
                <StatIconWrap $color="#5752e8"><BarChart3 size={16} /></StatIconWrap>
                TP Rank
              </Label>
              <Value>{rankDisplay}</Value>
            </Stat>
            <Stat $clickable onClick={handleOpenRestroomsModal}>
              <Label>
                <StatIconWrap $color="#2d6657"><FaToilet size={16} /></StatIconWrap>
                Restrooms Added
              </Label>
              <Value>{restroomsAddedCount}</Value>
            </Stat>
            <Stat $clickable onClick={handleOpenReviewsModal}>
              <Label>
                <StatIconWrap $color="#faaf40"><Star size={16} /></StatIconWrap>
                Restrooms Reviewed
              </Label>
              <Value>{restroomsReviewedCount}</Value>
            </Stat>
          </Grid>

          <BadgesAndProgressionRow>
            {/* DOM order keeps TP Progression first for desktop; CSS order
                lifts Badges above it on mobile (see BadgesColumn). */}
            <ProgressionColumn>
              <SectionHeader>
                <SectionTitle>
                  <Trophy size={16} />
                  TP Progression
                </SectionTitle>
              </SectionHeader>

              <LadderList>
                {titleLadder.map((level) => {
                  const reached = Number(totalTP || 0) >= Number(level.minTP);
                  const active = level.name === title;
                  return (
                    <LadderRow key={level.name} $active={active}>
                      <LadderName $active={active} $reached={reached}>
                        {active ? <Crown size={14} /> : reached ? <Trophy size={14} /> : <Lock size={14} />}
                        {level.name}
                      </LadderName>
                      <LadderTP $active={active} $reached={reached}>
                        {Number(level.minTP).toLocaleString()} TP
                      </LadderTP>
                    </LadderRow>
                  );
                })}
              </LadderList>
            </ProgressionColumn>

            <BadgesColumn>
              <SectionHeader>
                <SectionTitle>
                  <Award size={16} />
                  Badges Earned
                </SectionTitle>
                <HelpLink onClick={() => setShowBadgesHelp(true)}>
                  <HelpCircle size={14} />
                  What are badges?
                </HelpLink>
              </SectionHeader>

              <BadgeGrid>
                {BADGE_CATEGORIES.map((category) => {
                  const tier = getHighestTierForCategory(category.id, unlockedThresholdsByCategory);
                  const locked = !tier;

                  return (
                    <BadgeItem key={category.id}>
                      <BadgeButton
                        type="button"
                        onClick={() => {
                          setSelectedCategoryId(category.id);
                          ensureUserRestroomsLoaded();
                        }}
                      >
                        <BadgeIconWrap $locked={locked}>
                          {locked ? (
                            <img src={getBadgeLockImagePath(category)} alt={`${category.label} locked badge`} />
                          ) : (
                            <img src={getBadgeImagePath(category, tier)} alt={`${category.label} ${tier} badge`} />
                          )}
                        </BadgeIconWrap>
                        <BadgeCategoryLabel $locked={locked}>{category.label}</BadgeCategoryLabel>
                      </BadgeButton>
                    </BadgeItem>
                  );
                })}
              </BadgeGrid>
            </BadgesColumn>
          </BadgesAndProgressionRow>

        </Card>
      )}

      {shareMessage && (
        <ShareToast>
          <Check size={16} />
          {shareMessage}
        </ShareToast>
      )}

      {showBadgesHelp && (
        <ModalOverlay onClick={() => setShowBadgesHelp(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowBadgesHelp(false)}>
              <X size={16} />
            </ModalCloseButton>
            <ModalTitle>
              <Award size={22} />
              Earn badges by reviewing restrooms in different categories.
            </ModalTitle>

            <BadgeHelpContent $onDark />
            </ModalCard>
        </ModalOverlay>
      )}

      {selectedCategory && (() => {
        const currentTier = getHighestTierForCategory(selectedCategory.id, unlockedThresholdsByCategory);
        const locked = !currentTier;
        const count = Number(categoryCounts[selectedCategory.id] || 0);
        const currentTierIndex = currentTier ? TIER_ORDER.indexOf(currentTier) : -1;
        const nextTier = TIER_ORDER[currentTierIndex + 1];
        const isMaxed = currentTier === 'gold';

        const prevThreshold = currentTier ? TIER_REQUIREMENT[currentTier] : 0;
        const nextThreshold = nextTier ? TIER_REQUIREMENT[nextTier] : prevThreshold;
        // One segment per review needed for the next tier (or the max tier, once
        // reached). Filled segments = reviews earned, so 1 review always shows
        // as 1 filled segment.
        const segmentCount = nextThreshold;
        const filledSegments = Math.min(count, segmentCount);

        const currentTierName = currentTier
          ? badgeTierNames?.[selectedCategory.id]?.[String(TIER_REQUIREMENT[currentTier])] || null
          : null;
        const nextTierName = nextTier
          ? badgeTierNames?.[selectedCategory.id]?.[String(TIER_REQUIREMENT[nextTier])] || null
          : null;

        return (
          <ModalOverlay onClick={() => setSelectedCategoryId(null)}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalCloseButton onClick={() => setSelectedCategoryId(null)}>
                <X size={16} />
              </ModalCloseButton>

              <DetailBadgeWrap $locked={locked}>
                {locked ? (
                  <img
                    src={getBadgeLockImagePath(selectedCategory)}
                    alt={`${selectedCategory.label} locked badge`}
                  />
                ) : (
                  <img
                    src={getBadgeImagePath(selectedCategory, currentTier)}
                    alt={`${selectedCategory.label} ${currentTier} badge`}
                  />
                )}
              </DetailBadgeWrap>

              <ModalTitle style={{ justifyContent: 'center' }}>{selectedCategory.label}</ModalTitle>

              <div style={{ textAlign: 'center' }}>
                {currentTier ? (
                  <DetailTierBadge $color={TIER_COLOR[currentTier]}>{currentTier}</DetailTierBadge>
                ) : (
                  <DetailTierBadge $color="#a0a0a0">Locked</DetailTierBadge>
                )}
                {currentTierName && <DetailTierName>{currentTierName}</DetailTierName>}
              </div>

              <ProgressLabel>
                <span>{count} review{count === 1 ? '' : 's'}</span>
                <span>{isMaxed ? 'Max tier reached' : `${nextThreshold} reviews for ${nextTierName || nextTier}`}</span>
              </ProgressLabel>
              <ProgressBarTrack>
                <ProgressBarFill
                  style={{
                    width: `${segmentCount > 0 ? Math.min(100, (filledSegments / segmentCount) * 100) : 0}%`,
                  }}
                />
              </ProgressBarTrack>

              {isMaxed ? (
                <MaxedText>You've reached the highest tier for this category!</MaxedText>
              ) : (
                <NextTierRow>
                  <NextTierImg
                    src={getBadgeImagePath(selectedCategory, nextTier)}
                    alt={`${selectedCategory.label} ${nextTier} badge`}
                  />
                  <NextTierText>
                    <strong>Next: {nextTierName || nextTier}</strong>
                    <span>
                      {Math.max(0, nextThreshold - count)} more review
                      {Math.max(0, nextThreshold - count) === 1 ? '' : 's'} to unlock
                    </span>
                  </NextTierText>
                </NextTierRow>
              )}

              <CategoryRestroomsSection>
                <CategoryRestroomsTitle>
                  Reviews ({categoryRestrooms.length})
                </CategoryRestroomsTitle>

                {loadingRestrooms ? (
                  <Message>Loading restrooms...</Message>
                ) : categoryRestrooms.length === 0 ? (
                  <CategoryRestroomsEmpty>
                    No restrooms added in this category yet.
                  </CategoryRestroomsEmpty>
                ) : (
                  <CategoryRestroomsList>
                    {categoryRestrooms.map((restroom) => (
                      <CategoryRestroomItem
                        key={restroom.id}
                        onClick={() => handleRestroomClick(restroom)}
                      >
                        <span>{restroom.name || 'Unnamed Restroom'}</span>
                        <small>Added {getRelativeTime(restroom.createdAt)}</small>
                      </CategoryRestroomItem>
                    ))}
                  </CategoryRestroomsList>
                )}
              </CategoryRestroomsSection>
            </ModalCard>
          </ModalOverlay>
        );
      })()}
      {showRestroomsModal && (
        <ModalOverlay onClick={() => setShowRestroomsModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowRestroomsModal(false)}>
              <X size={16} />
            </ModalCloseButton>

            <ModalTitle>
              <FaToilet size={20} color="#2d6657" />
              {profile?.username ? `${profile.username}'s Restrooms` : 'Restrooms Added'} (
              {userRestrooms ? userRestrooms.length : profile?.restroomsAdded || profile?.restroomsCreated || 0})
            </ModalTitle>

            {loadingRestrooms ? (
              <Message>Loading restrooms...</Message>
            ) : !userRestrooms || userRestrooms.length === 0 ? (
              <Message>No restrooms added yet.</Message>
            ) : (
              <RestroomsListContainer>
                {sortedUserRestrooms.map((restroom) => (
                  <RestroomCard key={restroom.id} onClick={() => handleRestroomClick(restroom)}>
                    <RestroomCardTitle>{restroom.name || 'Unnamed Restroom'}</RestroomCardTitle>
                    <RestroomCardMeta>
                      <RestroomRating>
                        {'💩'.repeat(Math.max(1, Math.round(restroom.averageRating || 3)))}
                      </RestroomRating>
                      <span>Added {getRelativeTime(restroom.createdAt)}</span>
                    </RestroomCardMeta>
                  </RestroomCard>
                ))}
              </RestroomsListContainer>
            )}
          </ModalCard>
        </ModalOverlay>
      )}
      {showReviewsModal && (
        <ModalOverlay onClick={() => setShowReviewsModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setShowReviewsModal(false)}>
              <X size={16} />
            </ModalCloseButton>

            <ModalTitle>
              <Star size={20} color="#faaf40" />
              {profile?.username ? `${profile.username}'s Reviews` : 'Restrooms Reviewed'} ({restroomsReviewedCount})
            </ModalTitle>

            {loadingReviews ? (
              <Message>Loading reviews...</Message>
            ) : !userReviews || userReviews.length === 0 ? (
              <Message>No reviews written yet.</Message>
            ) : (
              <ReviewsListContainer>
                {sortedUserReviews.map((review) => (
                  <ReviewCard key={review.id} onClick={() => handleReviewClick(review)}>
                    <ReviewCardTitle>{review.restroomName || 'Restroom'}</ReviewCardTitle>
                    <ReviewCardRating>
                      {'💩'.repeat(Math.max(1, Math.round(review.rating || 5)))}
                    </ReviewCardRating>
                    {review.comment && <ReviewCardComment>{review.comment}</ReviewCardComment>}
                    <ReviewCardDate>{getRelativeTime(review.createdAt)}</ReviewCardDate>
                  </ReviewCard>
                ))}
              </ReviewsListContainer>
            )}
          </ModalCard>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default UserProfileView;
